import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import StatusPill from '@/components/StatusPill';
import { Plus, RefreshCw, Trash2, Edit3, Pause, Play, Wrench, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const SERVICE_TYPES = ['app', 'suite', 'worship', 'database', 'custom'];

function ServiceForm({ initial, onSave, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    initial || {
      name: '', type: 'app', description: '', health_url: '', interval_seconds: 60,
      expected_status: 200, timeout_seconds: 10, environment: 'production', version: '1.0.0',
    },
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await onSave(form);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="service-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2 col-span-2">
          <Label>{t('service_name')}</Label>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="service-form-name" />
        </div>
        <div className="space-y-2">
          <Label>{t('service_type')}</Label>
          <Select value={form.type} onValueChange={(v) => set('type', v)}>
            <SelectTrigger data-testid="service-form-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SERVICE_TYPES.map((tp) => (<SelectItem key={tp} value={tp}>{tp}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('service_environment')}</Label>
          <Select value={form.environment} onValueChange={(v) => set('environment', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="production">production</SelectItem>
              <SelectItem value="staging">staging</SelectItem>
              <SelectItem value="dev">dev</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 col-span-2">
          <Label>{t('service_url')}</Label>
          <Input value={form.health_url} onChange={(e) => set('health_url', e.target.value)} required
            placeholder="https://api.example.com/health" data-testid="service-form-url" />
        </div>
        <div className="space-y-2">
          <Label>{t('service_interval')}</Label>
          <Input type="number" min={10} value={form.interval_seconds}
            onChange={(e) => set('interval_seconds', parseInt(e.target.value || 60, 10))} />
        </div>
        <div className="space-y-2">
          <Label>{t('service_expected')}</Label>
          <Input type="number" value={form.expected_status}
            onChange={(e) => set('expected_status', parseInt(e.target.value || 200, 10))} />
        </div>
        <div className="space-y-2">
          <Label>{t('service_timeout')}</Label>
          <Input type="number" value={form.timeout_seconds}
            onChange={(e) => set('timeout_seconds', parseInt(e.target.value || 10, 10))} />
        </div>
        <div className="space-y-2">
          <Label>{t('service_version')}</Label>
          <Input value={form.version} onChange={(e) => set('version', e.target.value)} />
        </div>
        <div className="space-y-2 col-span-2">
          <Label>{t('service_description')}</Label>
          <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('cancel')}</Button>
        <Button type="submit" data-testid="service-form-submit">{t('save')}</Button>
      </DialogFooter>
    </form>
  );
}

export default function ServicesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const canDelete = hasRole(user, ['admin']);
  const [services, setServices] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/services');
    setServices(data);
  };
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  const create = async (form) => {
    try { await api.post('/services', form); toast.success(t('success')); setOpenCreate(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };
  const update = async (form) => {
    try { await api.patch(`/services/${editing.id}`, form); toast.success(t('success')); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };
  const del = async (id) => {
    if (!window.confirm('Confirm delete?')) return;
    try { await api.delete(`/services/${id}`); toast.success(t('success')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };
  const check = async (id) => {
    try { await api.post(`/services/${id}/check`); toast.success(t('success')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };
  const togglePause = async (s) => {
    await api.patch(`/services/${s.id}`, { paused: !s.paused }); load();
  };
  const toggleMaint = async (s) => {
    await api.patch(`/services/${s.id}`, { maintenance_mode: !s.maintenance_mode }); load();
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('nav_services')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{services.length} {t('nav_services').toLowerCase()}</p>
        </div>
        {canEdit && (
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="services-create-button"><Plus className="h-4 w-4 mr-2" /> {t('service_new')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{t('service_new')}</DialogTitle></DialogHeader>
              <ServiceForm onSave={create} onCancel={() => setOpenCreate(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="bg-card border border-border">
        <table className="w-full text-sm" data-testid="data-table-services">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
              <th className="px-4 py-3">{t('service_name')}</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">{t('service_environment')}</th>
              <th className="px-4 py-3">{t('service_version')}</th>
              <th className="px-4 py-3">{t('service_url')}</th>
              <th className="px-4 py-3">Latency</th>
              <th className="px-4 py-3">Last check</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`data-table-row-services-${s.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/services/${s.id}`} className="font-medium hover:text-[hsl(var(--primary))]">
                    {s.name}
                  </Link>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono">{s.type}</div>
                </td>
                <td className="px-4 py-3"><StatusPill status={s.maintenance_mode ? 'maintenance' : s.paused ? 'paused' : (s.current_status || 'unknown')} name={s.id} /></td>
                <td className="px-4 py-3 font-mono text-xs">{s.environment}</td>
                <td className="px-4 py-3 font-mono text-xs">v{s.version}</td>
                <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">
                  {s.health_url}
                </td>
                <td className="px-4 py-3 font-mono tabular-nums">{s.last_latency_ms ? `${Math.round(s.last_latency_ms)} ms` : '—'}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">
                  {s.last_checked ? formatDistanceToNow(new Date(s.last_checked), { addSuffix: true }) : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => check(s.id)} data-testid={`service-check-${s.id}`}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => togglePause(s)} data-testid={`service-pause-${s.id}`}>
                        {s.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => toggleMaint(s)} data-testid={`service-maint-${s.id}`}>
                        <Wrench className={`h-3.5 w-3.5 ${s.maintenance_mode ? 'text-sky-400' : ''}`} />
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`service-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing?.name}</DialogTitle></DialogHeader>
          {editing && <ServiceForm initial={editing} onSave={update} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
